import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { messagingApi } from '@/api/ops';
import type { ComplianceProfile, LegacyConcept, MessagingProvider, MessagingRoute } from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { Select, Segmented } from '@/components/Select';
import { StatusChip } from '@/components/ops/Badges';
import { CheckboxField, ReasonField, TextAreaField, TextField, reasonOk, splitList } from '@/components/ops/Forms';
import { PageHeader, Section, SubTabs } from '@/components/ops/Layout';
import { ConfirmDialog, Modal } from '@/components/ops/Modal';
import { EnvironmentPicker } from '@/components/ops/Pickers';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { MutationError, QueryState } from '@/components/ops/Problem';
import { CreateSenderIdForm, SenderIdsTable, senderIdsKey } from '@/components/ops/SenderIds';
import { formatCredits } from '@/ops/credits';
import { formatMinor, formatMs, formatNumber, formatRatio, formatTimestamp, localInputToIso, parseMajorToMinor } from '@/ops/format';
import { creditsAsReferenceMinor, fxRateAt, moneyAsCredits, useFxRates } from '@/ops/fx';
import { LIVE } from '@/ops/live';
import { useCreditScale } from '@/ops/useCreditScale';
import { useSelectedEnvironment } from '@/ops/useEnvironment';
import { TenantPicker } from '@/components/ops/TenantPicker';

type Tab = 'providers' | 'routes' | 'compliance' | 'sender-ids' | 'legacy' | 'spend' | 'dlr';

const mk = (env: string, ...rest: string[]) => ['ops', 'messaging', env, ...rest] as const;

/** A day as `YYYY-MM-DD`, `offset` days from today (UTC). */
function isoDay(offset = 0): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + offset);
  return d.toISOString().slice(0, 10);
}

/**
 * Providers & compliance (plan §6.6 / §7.3, D-CP20): provider rows with WRITE-ONLY credentials, routes,
 * per-country compliance profiles, the sender-id registry and its approvals, the wrapper's legacy
 * concepts, what we owe the provider, and delivery-receipt health. Live from messaging.
 */
export function MessagingAdminPage() {
  const { env, setEnv, environments } = useSelectedEnvironment();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab | null) ?? 'providers';
  const setTab = (t: Tab) => setParams((prev) => { const n = new URLSearchParams(prev); n.set('tab', t); return n; }, { replace: true });

  return (
    <RequirePermission permission={PERMISSIONS.messagingRead} what="Providers & compliance">
      <PageHeader title="Providers & compliance" subtitle="SMS / WhatsApp providers, routing and the regulatory data — console-entered, never constants."
        actions={<EnvironmentPicker env={env} environments={environments} onChange={setEnv} />} />
      <SubTabs<Tab> label="Messaging admin sections" active={tab} onChange={setTab} tabs={[
        { key: 'providers', label: 'Providers' },
        { key: 'routes', label: 'Routes' },
        { key: 'compliance', label: 'Compliance' },
        { key: 'sender-ids', label: 'Sender IDs' },
        { key: 'legacy', label: 'Legacy concepts' },
        { key: 'spend', label: 'Provider spend' },
        { key: 'dlr', label: 'Delivery receipts' },
      ]} />
      {env && tab === 'providers' && <ProvidersPanel env={env} />}
      {env && tab === 'routes' && <RoutesPanel env={env} />}
      {env && tab === 'compliance' && <CompliancePanel env={env} />}
      {env && tab === 'sender-ids' && <SenderIdsPanel env={env} />}
      {env && tab === 'legacy' && <LegacyPanel env={env} />}
      {env && tab === 'spend' && <SpendPanel env={env} />}
      {env && tab === 'dlr' && <DlrPanel env={env} />}
    </RequirePermission>
  );
}

// ─── Providers ────────────────────────────────────────────────────────────

function ProvidersPanel({ env }: { env: string }) {
  const providers = useQuery({ queryKey: mk(env, 'providers'), queryFn: () => messagingApi.providers(env), ...LIVE });
  const canManage = hasPermission(PERMISSIONS.messagingManage);
  const [editing, setEditing] = useState<MessagingProvider | 'new' | null>(null);
  const [probe, setProbe] = useState<{ key: string; kind: 'test' | 'balance'; text: string; ok: boolean } | null>(null);

  const test = useMutation({
    mutationFn: (key: string) => messagingApi.testProvider(env, key),
    onMutate: () => { setProbe(null); balance.reset(); },
    onSuccess: (r) => setProbe({ key: r.key, kind: 'test', ok: r.ok, text: r.detail }),
  });
  const balance = useMutation({
    mutationFn: (key: string) => messagingApi.providerBalance(env, key),
    onMutate: () => { setProbe(null); test.reset(); },
    onSuccess: (r) => setProbe({
      key: r.key,
      kind: 'balance',
      ok: r.supported,
      text: r.supported ? `${formatMinor(r.balance_minor, r.currency)}${r.detail ? ` — ${r.detail}` : ''}` : (r.detail ?? 'This provider does not report a balance.'),
    }),
    gcTime: 0,
  });

  return (
    <Section title="Providers" meta="credentials are write-only — encrypted by messaging, never shown"
      actions={canManage && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing('new')}>Add provider</button>} flush>
      <QueryState query={providers} service="messaging">
        {(data) => (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Provider</th><th>State</th><th>Modes</th><th>Credentials</th><th>DLR</th><th>Updated</th><th aria-label="Actions" /></tr></thead>
              <tbody>
                {data.providers.length === 0 && (
                  <tr><td colSpan={7} className="empty-state t-body">No providers yet — SMS cannot be sent until one is added and enabled.</td></tr>
                )}
                {data.providers.map((p) => (
                  <tr key={p.key}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{p.display_name}</div>
                      <div className="t-caption muted mono">{p.key} · {p.kind}</div>
                      {p.notes && <div className="t-caption subtle wrap">{p.notes}</div>}
                    </td>
                    <td>
                      <StatusChip status={p.enabled ? 'enabled' : 'disabled'} />
                      {!p.adapter_registered && <div className="t-caption note-error">no adapter registered — unavailable</div>}
                    </td>
                    <td className="t-caption">{p.account_mode}<div className="muted">DLR {p.dlr_mode}</div></td>
                    <td>
                      {p.credentials_set
                        ? <StatusChip status="set" tone="success" title={p.credentials_updated_at ? `updated ${formatTimestamp(p.credentials_updated_at)}` : undefined} />
                        : <StatusChip status="missing" tone="warning" />}
                    </td>
                    <td className="t-caption">
                      reconcile after {p.reconcile_after_minutes} min · expire after {p.expire_after_hours} h
                      <div className="muted">{p.dlr_ip_allowlist.length ? `IPs: ${p.dlr_ip_allowlist.join(', ')}` : 'no IP allow-list'}</div>
                    </td>
                    <td className="t-caption">{formatTimestamp(p.updated_at)}<div className="muted">{p.updated_by}</div></td>
                    <td>
                      <div className="row">
                        {canManage && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(p)}>Edit</button>}
                        {canManage && (
                          <button type="button" className="btn btn-ghost btn-sm" disabled={test.isPending} onClick={() => test.mutate(p.key)}>
                            {test.isPending && test.variables === p.key ? 'Testing…' : 'Test'}
                          </button>
                        )}
                        <button type="button" className="btn btn-ghost btn-sm" disabled={balance.isPending} onClick={() => balance.mutate(p.key)}>
                          {balance.isPending && balance.variables === p.key ? 'Reading…' : 'Balance'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryState>
      <div className="card-body stack-sm" style={{ paddingTop: 0 }}>
        <MutationError error={test.error} service="messaging" />
        <MutationError error={balance.error} service="messaging" />
        {probe && (
          <div className={`callout ${probe.ok ? 'callout-info' : 'callout-warning'}`} role="status">
            <span className="callout-icon" aria-hidden="true">{probe.ok ? 'i' : '!'}</span>
            <div className="t-body">
              <strong className="t-heading" style={{ display: 'block' }}>
                {probe.key} — {probe.kind === 'test' ? (probe.ok ? 'credential test passed' : 'credential test failed') : 'balance'}
              </strong>
              {probe.text}
              <button type="button" className="btn btn-ghost btn-sm" style={{ marginInlineStart: 8 }} onClick={() => setProbe(null)}>Dismiss</button>
            </div>
          </div>
        )}
      </div>
      {editing && <ProviderForm env={env} provider={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </Section>
  );
}

function ProviderForm({ env, provider, onClose }: { env: string; provider: MessagingProvider | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const creating = provider === null;
  const [key, setKey] = useState(provider?.key ?? '');
  const [kind, setKind] = useState<'sms' | 'whatsapp'>(provider?.kind === 'whatsapp' ? 'whatsapp' : 'sms');
  const [name, setName] = useState(provider?.display_name ?? '');
  const [enabled, setEnabled] = useState(provider?.enabled ?? false);
  const [accountMode, setAccountMode] = useState<'prepaid' | 'postpaid'>(provider?.account_mode === 'postpaid' ? 'postpaid' : 'prepaid');
  const [dlrMode, setDlrMode] = useState(provider?.dlr_mode ?? 'webhook');
  const [endpoints, setEndpoints] = useState(provider?.endpoints ? JSON.stringify(provider.endpoints, null, 2) : '{}');
  const [publicCreds, setPublicCreds] = useState(provider?.credentials_public ? JSON.stringify(provider.credentials_public, null, 2) : '{}');
  const [apiKey, setApiKey] = useState('');
  const [userName, setUserName] = useState('');
  const [ips, setIps] = useState((provider?.dlr_ip_allowlist ?? []).join(', '));
  const [reconcile, setReconcile] = useState(String(provider?.reconcile_after_minutes ?? 120));
  const [expire, setExpire] = useState(String(provider?.expire_after_hours ?? 72));
  const [notes, setNotes] = useState(provider?.notes ?? '');

  const parseJson = (text: string): { ok: true; value: unknown } | { ok: false } => {
    try { return { ok: true, value: text.trim() ? JSON.parse(text) : {} }; } catch { return { ok: false }; }
  };
  const endpointsJson = parseJson(endpoints);
  const publicJson = parseJson(publicCreds);
  // Both credential fields or neither: half a credential would overwrite the stored pair with a blank.
  const credentialsComplete = (apiKey === '') === (userName === '');
  const valid = (!creating || /^[a-z][a-z0-9_]{1,31}$/.test(key)) && name.trim() !== '' && endpointsJson.ok && publicJson.ok &&
    Number.isInteger(Number(reconcile)) && Number(reconcile) >= 5 && Number(reconcile) <= 10080 &&
    Number.isInteger(Number(expire)) && Number(expire) >= 1 && Number(expire) <= 720 && credentialsComplete;

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        kind,
        display_name: name.trim(),
        enabled,
        account_mode: accountMode,
        dlr_mode: dlrMode,
        endpoints: endpointsJson.ok ? endpointsJson.value : undefined,
        credentials_public: publicJson.ok ? publicJson.value : undefined,
        dlr_ip_allowlist: splitList(ips),
        reconcile_after_minutes: Number(reconcile),
        expire_after_hours: Number(expire),
        // messaging reads null as "keep" and '' as "clear": an emptied field must clear.
        notes: notes.trim(),
      };
      // Write-only: sent only when typed; omitted = the stored (encrypted) credentials stay.
      if (apiKey || userName) body.credentials = { api_key: apiKey, user_name: userName };
      return creating ? messagingApi.createProvider(env, { ...body, key: key.trim() }) : messagingApi.updateProvider(env, provider!.key, body);
    },
    onSuccess: () => {
      setApiKey('');
      setUserName('');
      queryClient.invalidateQueries({ queryKey: mk(env, 'providers') });
      onClose();
    },
  });

  return (
    <Modal title={creating ? 'Add a provider' : `Edit ${provider!.display_name}`} size="lg" onClose={save.isPending ? undefined : onClose} dismissible={!save.isPending}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={save.isPending}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save provider'}</button>
        </>
      }>
      <div className="form-grid">
        <TextField label="Key" value={key} onChange={setKey} mono disabled={!creating} hint="The adapter key, e.g. cequens." />
        <TextField label="Display name" value={name} onChange={setName} required />
        <Segmented label="Kind" value={kind} onChange={setKind} options={[{ value: 'sms', label: 'SMS' }, { value: 'whatsapp', label: 'WhatsApp' }]} />
        <Segmented label="Account mode" value={accountMode} onChange={setAccountMode} options={[{ value: 'prepaid', label: 'Prepaid' }, { value: 'postpaid', label: 'Postpaid' }]} />
        <Select label="DLR mode" value={dlrMode} onChange={setDlrMode} searchable={false}
          options={[{ value: 'webhook', label: 'webhook (tokenized URL)' }, { value: 'poll', label: 'poll' }, { value: 'none', label: 'none' }]} />
        <TextField label="Reconcile after (min)" value={reconcile} onChange={setReconcile} type="number" min={5} max={10080}
          hint="5 to 10,080 minutes." />
        <TextField label="Expire after (h)" value={expire} onChange={setExpire} type="number" min={1} max={720} hint="1 to 720 hours." />
      </div>
      <CheckboxField label="Enabled" checked={enabled} onChange={setEnabled} hint="Enable only after a credential test passes." />
      <h3 className="t-heading" style={{ margin: 0 }}>Credentials (write-only)</h3>
      <p className="t-caption muted" style={{ margin: 0 }}>
        {provider?.credentials_set ? 'Credentials are set. Leave both fields empty to keep them; type both to replace them.' : 'Encrypted by messaging before storage; never returned by any read.'}
      </p>
      <div className="form-grid">
        <TextField label="API key" type="password" value={apiKey} onChange={setApiKey} mono placeholder={provider?.credentials_set ? '•••••• keep current' : ''} />
        <TextField label="User name" type="password" value={userName} onChange={setUserName} mono placeholder={provider?.credentials_set ? '•••••• keep current' : ''} />
      </div>
      <TextAreaField label="Endpoints (JSON)" value={endpoints} onChange={setEndpoints} mono rows={3} error={endpointsJson.ok ? null : 'Not valid JSON.'}
        hint='e.g. {"base_url": "https://apis.cequens.com"}' />
      <TextAreaField label="Public credential fields (JSON)" value={publicCreds} onChange={setPublicCreds} mono rows={2} error={publicJson.ok ? null : 'Not valid JSON.'} />
      <TextField label="DLR IP allow-list" value={ips} onChange={setIps} mono hint="Comma separated; empty = the URL token alone authenticates." />
      <TextField label="Notes" value={notes} onChange={setNotes} />
      <MutationError error={save.error} service="messaging" />
    </Modal>
  );
}

// ─── Routes ───────────────────────────────────────────────────────────────

function RoutesPanel({ env }: { env: string }) {
  const queryClient = useQueryClient();
  const routes = useQuery({ queryKey: mk(env, 'routes'), queryFn: () => messagingApi.routes(env), ...LIVE });
  const providers = useQuery({ queryKey: mk(env, 'providers'), queryFn: () => messagingApi.providers(env), ...LIVE });
  const canManage = hasPermission(PERMISSIONS.messagingManage);
  const [editing, setEditing] = useState<MessagingRoute | 'new' | null>(null);
  const [deleting, setDeleting] = useState<MessagingRoute | null>(null);
  const remove = useMutation({
    mutationFn: () => messagingApi.deleteRoute(env, deleting!.id),
    onSuccess: () => { setDeleting(null); queryClient.invalidateQueries({ queryKey: mk(env, 'routes') }); },
  });

  return (
    <Section title="Routes" meta="country + purpose + sender → provider, by priority; failover only on a transient failure"
      actions={canManage && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing('new')}>Add route</button>} flush>
      <QueryState query={routes} service="messaging">
        {(data) => data.routes.length === 0 ? (
          <div className="empty-state t-body">No routes — every send answers SMS_NO_ROUTE (422).</div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Country</th><th>Purpose</th><th>Sender</th><th>Provider</th><th className="num">Priority</th><th>State</th>{canManage && <th aria-label="Actions" />}</tr></thead>
              <tbody>
                {[...data.routes].sort((a, b) => a.country.localeCompare(b.country) || a.priority - b.priority).map((r) => (
                  <tr key={r.id} className={r.enabled ? undefined : 'row-muted'}>
                    <td className="mono">{r.country}</td>
                    <td className="mono t-caption">{r.purpose ?? 'any'}</td>
                    <td className="mono t-caption">{r.sender_id ?? 'any'}</td>
                    <td className="mono">{r.provider_key}</td>
                    <td className="num">{r.priority}</td>
                    <td><StatusChip status={r.enabled ? 'enabled' : 'disabled'} /></td>
                    {canManage && (
                      <td><div className="row">
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(r)}>Edit</button>
                        <button type="button" className="btn btn-ghost btn-sm is-danger" onClick={() => setDeleting(r)}>Delete</button>
                      </div></td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryState>
      {editing && <RouteForm env={env} route={editing === 'new' ? null : editing} providers={providers.data?.providers ?? []} onClose={() => setEditing(null)} />}
      {deleting && (
        <ConfirmDialog title={`Delete the ${deleting.country} route to ${deleting.provider_key}?`} confirmLabel="Delete route" destructive
          pending={remove.isPending} onConfirm={() => remove.mutate()} onCancel={() => { setDeleting(null); remove.reset(); }}>
          <p className="t-body" style={{ margin: 0 }}>Sends that matched only this route will answer SMS_NO_ROUTE until another route covers them.</p>
          <MutationError error={remove.error} service="messaging" />
        </ConfirmDialog>
      )}
    </Section>
  );
}

function RouteForm({ env, route, providers, onClose }: { env: string; route: MessagingRoute | null; providers: MessagingProvider[]; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [country, setCountry] = useState(route?.country ?? 'EG');
  const [purpose, setPurpose] = useState(route?.purpose ?? '');
  const [sender, setSender] = useState(route?.sender_id ?? '');
  const [provider, setProvider] = useState(route?.provider_key ?? providers[0]?.key ?? '');
  const [priority, setPriority] = useState(String(route?.priority ?? 100));
  const [enabled, setEnabled] = useState(route?.enabled ?? true);
  const valid = /^([A-Za-z]{2}|\*)$/.test(country) && provider !== '' && Number.isInteger(Number(priority));
  const save = useMutation({
    mutationFn: () => {
      const body = {
        // '' clears (null would keep the stored restriction): "Any purpose" / no sender must stick.
        country: country.toUpperCase(), purpose, sender_id: sender.trim(),
        provider_key: provider, priority: Number(priority), enabled,
      };
      return route ? messagingApi.updateRoute(env, route.id, body) : messagingApi.createRoute(env, body);
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: mk(env, 'routes') }); onClose(); },
  });
  return (
    <Modal title={route ? 'Edit route' : 'Add route'} onClose={save.isPending ? undefined : onClose} dismissible={!save.isPending}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={save.isPending}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save route'}</button>
        </>
      }>
      <div className="form-grid">
        <TextField label="Country" value={country} onChange={setCountry} mono hint="ISO-2, or * for any." />
        <Select label="Purpose" value={purpose} onChange={setPurpose} placeholder="Any purpose" searchable={false}
          options={['transactional', 'marketing', 'otp'].map((p) => ({ value: p, label: p }))} />
        <TextField label="Sender id" value={sender} onChange={setSender} mono hint="Empty = any sender." />
        <Select label="Provider" value={provider} onChange={setProvider} options={providers.map((p) => ({ value: p.key, label: p.display_name, hint: p.enabled ? undefined : 'disabled' }))} />
        <TextField label="Priority" value={priority} onChange={setPriority} type="number" hint="Lower is tried first." />
      </div>
      <CheckboxField label="Enabled" checked={enabled} onChange={setEnabled} />
      <MutationError error={save.error} service="messaging" />
    </Modal>
  );
}

// ─── Compliance profiles ──────────────────────────────────────────────────

function CompliancePanel({ env }: { env: string }) {
  const profiles = useQuery({ queryKey: mk(env, 'compliance'), queryFn: () => messagingApi.complianceProfiles(env), ...LIVE });
  const canManage = hasPermission(PERMISSIONS.messagingManage);
  const [editing, setEditing] = useState<ComplianceProfile | 'new' | null>(null);

  return (
    <Section title="Compliance profiles" meta="marketing quiet window, daily promotional cap, opt-out keywords — per destination country"
      actions={canManage && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing('new')}>Add country</button>} flush>
      <QueryState query={profiles} service="messaging">
        {(data) => data.profiles.length === 0 ? (
          <div className="empty-state t-body">No profiles — marketing to any country is unrestricted.</div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Country</th><th>Marketing allowed</th><th className="num">Promos / recipient / day</th><th>STOP · HELP</th><th>Opt-out line</th><th className="num">Opt-out SLA</th>{canManage && <th aria-label="Actions" />}</tr></thead>
              <tbody>
                {data.profiles.map((p) => (
                  <tr key={p.country}>
                    <td className="mono">{p.country}</td>
                    <td className="t-caption">{p.marketing_window_start}–{p.marketing_window_end}<div className="muted">{p.time_zone}</div></td>
                    <td className="num">{p.max_promotional_per_recipient_per_day === 0 ? 'no cap' : p.max_promotional_per_recipient_per_day}</td>
                    <td className="t-caption">{p.stop_keywords.join(', ')}<div className="muted">{p.help_keywords.join(', ')}</div></td>
                    <td className="t-caption">{p.opt_out_line_en}<div className="muted" dir="rtl">{p.opt_out_line_ar}</div></td>
                    <td className="num">{p.opt_out_sla_hours} h</td>
                    {canManage && <td><button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(p)}>Edit</button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryState>
      {editing && <ComplianceForm env={env} profile={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </Section>
  );
}

function ComplianceForm({ env, profile, onClose }: { env: string; profile: ComplianceProfile | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [country, setCountry] = useState(profile?.country ?? '');
  const [start, setStart] = useState(profile?.marketing_window_start ?? '09:00');
  const [end, setEnd] = useState(profile?.marketing_window_end ?? '21:00');
  const [tz, setTz] = useState(profile?.time_zone ?? 'Africa/Cairo');
  const [cap, setCap] = useState(String(profile?.max_promotional_per_recipient_per_day ?? 1));
  const [stop, setStop] = useState((profile?.stop_keywords ?? ['STOP', 'إلغاء']).join(', '));
  const [help, setHelp] = useState((profile?.help_keywords ?? ['HELP', 'مساعدة']).join(', '));
  const [lineEn, setLineEn] = useState(profile?.opt_out_line_en ?? '');
  const [lineAr, setLineAr] = useState(profile?.opt_out_line_ar ?? '');
  const [sla, setSla] = useState(String(profile?.opt_out_sla_hours ?? 24));
  const time = /^([01]\d|2[0-3]):[0-5]\d$/;
  const valid = /^[A-Za-z]{2}$/.test(country) && time.test(start) && time.test(end) && tz.trim() !== '' &&
    Number.isInteger(Number(cap)) && Number(cap) >= 0 && Number(cap) <= 100 &&
    Number.isInteger(Number(sla)) && Number(sla) >= 1 && Number(sla) <= 720 && lineEn.trim() !== '' && lineAr.trim() !== '';
  const save = useMutation({
    mutationFn: () => messagingApi.putComplianceProfile(env, country.toUpperCase(), {
      marketing_window_start: start, marketing_window_end: end, time_zone: tz.trim(),
      max_promotional_per_recipient_per_day: Number(cap), stop_keywords: splitList(stop), help_keywords: splitList(help),
      opt_out_line_en: lineEn.trim(), opt_out_line_ar: lineAr.trim(), opt_out_sla_hours: Number(sla),
    }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: mk(env, 'compliance') }); onClose(); },
  });
  return (
    <Modal title={profile ? `Compliance — ${profile.country}` : 'Add a compliance profile'} size="lg" onClose={save.isPending ? undefined : onClose} dismissible={!save.isPending}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={save.isPending}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save profile'}</button>
        </>
      }>
      <div className="form-grid">
        <TextField label="Country (ISO-2)" value={country} onChange={setCountry} mono disabled={!!profile} />
        <TextField label="Marketing allowed from" value={start} onChange={setStart} type="time" />
        <TextField label="Marketing allowed until" value={end} onChange={setEnd} type="time" />
        <TextField label="Time zone" value={tz} onChange={setTz} mono />
        <TextField label="Promotional messages / recipient / day" value={cap} onChange={setCap} type="number" min={0} max={100}
          hint="0 = no cap. At most 100." />
        <TextField label="Opt-out SLA (hours)" value={sla} onChange={setSla} type="number" min={1} max={720} hint="1 to 720 hours." />
        <TextField label="STOP keywords" value={stop} onChange={setStop} hint="English and Arabic, comma separated." />
        <TextField label="HELP keywords" value={help} onChange={setHelp} />
      </div>
      <TextField label="Opt-out line (en)" value={lineEn} onChange={setLineEn} />
      <TextField label="Opt-out line (ar)" value={lineAr} onChange={setLineAr} />
      <p className="t-caption muted" style={{ margin: 0 }}>Transactional and OTP messages are never held by these rules — marketing only.</p>
      <MutationError error={save.error} service="messaging" />
    </Modal>
  );
}

// ─── Sender ids ───────────────────────────────────────────────────────────

const SENDER_STATUSES = ['draft', 'submitted', 'approved', 'rejected', 'suspended'];

function SenderIdsPanel({ env }: { env: string }) {
  const [status, setStatus] = useState('submitted');
  const [tenantId, setTenantId] = useState('');
  const [creating, setCreating] = useState(false);
  const tenantOk = true;
  const senderIds = useQuery({
    queryKey: [...senderIdsKey(env), 'all', status, tenantId],
    queryFn: () => messagingApi.senderIds(env, { status: status || undefined, tenant_id: tenantId.trim() || undefined }),
    enabled: tenantOk,
    ...LIVE,
  });

  return (
    <Section title="Sender-id registry" meta="submit → approve / reject → suspend; approval is console-only"
      actions={hasPermission(PERMISSIONS.messagingManage) && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCreating(true)}>Register sender id</button>} flush>
      <div className="card-body">
        <div className="filter-grid">
          <Select label="Status" value={status} onChange={setStatus} placeholder="Any status" searchable={false} options={SENDER_STATUSES.map((s) => ({ value: s, label: s }))} />
          <TenantPicker env={env} value={tenantId} onChange={setTenantId} />
        </div>
      </div>
      {tenantOk && (
        <QueryState query={senderIds} service="messaging">
          {(data) => <SenderIdsTable env={env} senderIds={data.sender_ids} />}
        </QueryState>
      )}
      {creating && <CreateSenderIdForm env={env} onClose={() => setCreating(false)} />}
    </Section>
  );
}

// ─── Legacy concepts ──────────────────────────────────────────────────────

function LegacyPanel({ env }: { env: string }) {
  const concepts = useQuery({ queryKey: mk(env, 'legacy'), queryFn: () => messagingApi.legacyConcepts(env), ...LIVE });
  const canManage = hasPermission(PERMISSIONS.messagingManage);
  const [editing, setEditing] = useState<LegacyConcept | 'new' | null>(null);

  return (
    <Section title="Legacy concepts" meta="the sms-services wrapper's conceptName → sender resolution, as data (in precedence order)"
      actions={canManage && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing('new')}>Add concept</button>} flush>
      <QueryState query={concepts} service="messaging">
        {(data) => (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th className="num">#</th><th>Concept</th><th>Match</th><th>Sender</th><th>Network overrides</th><th>Account</th><th>State</th>{canManage && <th aria-label="Actions" />}</tr></thead>
              <tbody>
                {data.concepts.length === 0 && (
                  <tr><td colSpan={canManage ? 8 : 7} className="empty-state t-body">No legacy concepts — the compat API answers no concept.</td></tr>
                )}
                {[...data.concepts].sort((a, b) => a.precedence - b.precedence).map((c) => (
                  <tr key={c.concept} className={c.enabled ? undefined : 'row-muted'}>
                    <td className="num">{c.precedence}</td>
                    <td>{c.concept}{c.note && <div className="t-caption muted">{c.note}</div>}</td>
                    <td className="t-caption">{c.match_mode}</td>
                    <td className="mono">{c.sender_id}</td>
                    <td className="t-caption mono">{c.network_overrides.length ? c.network_overrides.map((o) => `${o.national_prefix}→${o.sender_id}`).join(', ') : '—'}</td>
                    <td className="t-caption mono">{c.account_external_ref}{c.verify_template_code && <div className="muted">verify {c.verify_template_code}</div>}</td>
                    <td><StatusChip status={c.enabled ? 'enabled' : 'disabled'} /></td>
                    {canManage && <td><button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(c)}>Edit</button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryState>
      {editing && <LegacyForm env={env} concept={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </Section>
  );
}

function LegacyForm({ env, concept, onClose }: { env: string; concept: LegacyConcept | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(concept?.concept ?? '');
  const [matchMode, setMatchMode] = useState<'substring' | 'exact'>(concept?.match_mode === 'exact' ? 'exact' : 'substring');
  const [precedence, setPrecedence] = useState(String(concept?.precedence ?? 100));
  const [sender, setSender] = useState(concept?.sender_id ?? '');
  const [overrides, setOverrides] = useState((concept?.network_overrides ?? []).map((o) => `${o.national_prefix}=${o.sender_id}`).join(', '));
  const [verify, setVerify] = useState(concept?.verify_template_code ?? '');
  const [enabled, setEnabled] = useState(concept?.enabled ?? true);
  const [note, setNote] = useState(concept?.note ?? '');
  const parsedOverrides = splitList(overrides).map((pair) => {
    const [prefix, value] = pair.split('=').map((x) => x.trim());
    return prefix && value ? { national_prefix: prefix, sender_id: value } : null;
  });
  const valid = name.trim() !== '' && sender.trim() !== '' && Number.isInteger(Number(precedence)) && parsedOverrides.every(Boolean);
  const save = useMutation({
    mutationFn: () => {
      const body = {
        concept: name.trim(), match_mode: matchMode, precedence: Number(precedence), sender_id: sender.trim(),
        // '' clears (null would keep the stored value).
        network_overrides: parsedOverrides, verify_template_code: verify.trim(), enabled, note: note.trim(),
      };
      return concept ? messagingApi.updateLegacyConcept(env, concept.concept, body) : messagingApi.createLegacyConcept(env, body);
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: mk(env, 'legacy') }); onClose(); },
  });
  return (
    <Modal title={concept ? `Edit “${concept.concept}”` : 'Add a legacy concept'} onClose={save.isPending ? undefined : onClose} dismissible={!save.isPending}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={save.isPending}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save concept'}</button>
        </>
      }>
      <p className="t-caption muted" style={{ margin: 0 }}>The compat facade must stay byte-compatible with the wrapper: change precedence and matching with care.</p>
      <div className="form-grid">
        <TextField label="Concept" value={name} onChange={setName} disabled={!!concept} />
        <Segmented label="Match" value={matchMode} onChange={setMatchMode} options={[{ value: 'substring', label: 'substring' }, { value: 'exact', label: 'exact' }]} />
        <TextField label="Precedence" value={precedence} onChange={setPrecedence} type="number" hint="The wrapper's if/else order." />
        <TextField label="Sender id" value={sender} onChange={setSender} mono />
        <TextField label="Verify template code" value={verify} onChange={setVerify} mono />
      </div>
      <TextField label="Network overrides" value={overrides} onChange={setOverrides} mono placeholder="015=ANYWARE" hint="national_prefix=sender, comma separated." />
      <CheckboxField label="Enabled" checked={enabled} onChange={setEnabled} />
      <TextField label="Note" value={note} onChange={setNote} />
      <MutationError error={save.error} service="messaging" />
    </Modal>
  );
}

// ─── Provider spend + payments ────────────────────────────────────────────

export function SpendPanel({ env }: { env: string }) {
  const [provider, setProvider] = useState('');
  const [from, setFrom] = useState(isoDay(-30));
  const [to, setTo] = useState(isoDay(0));
  const [recording, setRecording] = useState(false);
  const providers = useQuery({ queryKey: mk(env, 'providers'), queryFn: () => messagingApi.providers(env), ...LIVE });
  const spend = useQuery({
    queryKey: mk(env, 'spend', provider, from, to),
    queryFn: () => messagingApi.providerSpend(env, { provider: provider || undefined, from, to }),
    enabled: isFullDay(from) && isFullDay(to),
    ...LIVE,
  });
  const { scale } = useCreditScale(env);
  const { fx, allowed: seesFx } = useFxRates(env);
  const totals = useMemo(() => {
    const days = spend.data?.days ?? [];
    return days.reduce((acc, d) => ({ messages: acc.messages + d.messages, parts: acc.parts + d.parts, delivered: acc.delivered + d.delivered, failed: acc.failed + d.failed }),
      { messages: 0, parts: 0, delivered: 0, failed: 0 });
  }, [spend.data]);
  // What we paid, in the SAME unit as the cost: each payment at billing's FX rate on its day (a payment with no rate is
  // counted apart, never as 0).
  const paid = useMemo(() => {
    let credits = 0;
    let unconverted = 0;
    for (const p of spend.data?.payments ?? []) {
      const c = moneyAsCredits(fx, p.amount_minor, p.currency, p.paid_at);
      if (c === null) unconverted += 1;
      else credits += c;
    }
    return { credits, unconverted };
  }, [spend.data, fx]);

  return (
    <Section title="Provider spend" meta="what we owe / prepaid to the provider, reconciled against its statement"
      actions={hasPermission(PERMISSIONS.messagingManage) && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRecording(true)}>Record a payment</button>}>
      <div className="filter-grid" style={{ marginBottom: 16 }}>
        <Select label="Provider" value={provider} onChange={setProvider} placeholder="All providers" searchable={false}
          options={(providers.data?.providers ?? []).map((p) => ({ value: p.key, label: p.display_name }))} />
        <TextField label="From" value={from} onChange={setFrom} mono placeholder="YYYY-MM-DD" />
        <TextField label="To" value={to} onChange={setTo} mono placeholder="YYYY-MM-DD" />
      </div>
      <QueryState query={spend} service="messaging">
        {(data) => (
          <div className="stack">
            <div className="kpi-row">
              <div className="card stat-tile">
                <div className="stat-label t-label">Cost</div>
                <div className="t-display">{formatCredits(data.total_cost_credits, scale)}</div>
                <div className="stat-sub t-caption">
                  {fx ? `≈ ${formatMinor(creditsAsReferenceMinor(data.total_cost_credits, fx.reference_currency), fx.reference_currency)} · ` : ''}{data.from} → {data.to}
                </div>
              </div>
              <div className="card stat-tile">
                <div className="stat-label t-label">Paid to providers</div>
                <div className="t-display">{fx ? formatCredits(paid.credits, scale) : '—'}</div>
                <div className="stat-sub t-caption">
                  {!seesFx ? 'your role cannot read billing FX rates — amounts below in money only'
                    : !fx ? 'FX rates unavailable — amounts below in money only'
                      : paid.unconverted > 0 ? `${paid.unconverted} payment(s) in a currency with no FX rate are not included`
                        : `${data.payments.length} payment(s) at billing's FX rate on the day paid`}
                </div>
              </div>
              <div className="card stat-tile"><div className="stat-label t-label">Messages · parts</div><div className="t-display">{formatNumber(totals.messages)} · {formatNumber(totals.parts)}</div></div>
              <div className="card stat-tile"><div className="stat-label t-label">Delivered · failed</div><div className="t-display">{formatNumber(totals.delivered)} · {formatNumber(totals.failed)}</div></div>
            </div>
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Day</th><th>Provider</th><th className="num">Messages</th><th className="num">Parts</th><th className="num">Verifications</th><th className="num">Cost</th><th className="num">Delivered · failed · expired</th></tr></thead>
                <tbody>
                  {data.days.length === 0 && <tr><td colSpan={7} className="empty-state">No traffic in this range.</td></tr>}
                  {data.days.map((d) => (
                    <tr key={`${d.day}-${d.provider_key}`}>
                      <td className="t-caption">{d.day}</td>
                      <td className="mono t-caption">{d.provider_key}</td>
                      <td className="num">{formatNumber(d.messages)}</td>
                      <td className="num">{formatNumber(d.parts)}</td>
                      <td className="num">{formatNumber(d.verifications)}</td>
                      <td className="num">{formatCredits(d.cost_credits, scale)}</td>
                      <td className="num t-caption">{formatNumber(d.delivered)} · {formatNumber(d.failed)} · {formatNumber(d.expired)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <h3 className="t-heading" style={{ margin: 0 }}>Payments to providers</h3>
            {data.payments.length === 0 ? <div className="t-caption subtle">None recorded in this range.</div> : (
              <table className="table">
                <thead><tr><th>Paid</th><th>Provider</th><th className="num">Amount</th><th className="num">In credits</th><th>Reference</th><th>Recorded by</th></tr></thead>
                <tbody>
                  {data.payments.map((p) => {
                    const credits = moneyAsCredits(fx, p.amount_minor, p.currency, p.paid_at);
                    const rate = fxRateAt(fx, p.currency, p.paid_at);
                    return (
                    <tr key={p.id}>
                      <td className="t-caption">{formatTimestamp(p.paid_at)}</td>
                      <td className="mono t-caption">{p.provider_key}</td>
                      <td className="num">{formatMinor(p.amount_minor, p.currency)}</td>
                      <td className="num">
                        {credits === null ? <span className="subtle">{fx ? 'no FX rate' : '—'}</span> : formatCredits(credits, scale)}
                        {credits !== null && rate !== null && rate !== 1 && fx && (
                          <div className="t-caption muted">at {rate.toLocaleString('en-US', { maximumFractionDigits: 6 })} {p.currency} per {fx.reference_currency}</div>
                        )}
                      </td>
                      <td className="t-caption">{p.reference ?? '—'}{p.note && <div className="muted">{p.note}</div>}</td>
                      <td className="t-caption">{p.created_by}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </QueryState>
      {recording && <ProviderPaymentForm env={env} providers={providers.data?.providers ?? []} onClose={() => setRecording(false)} />}
    </Section>
  );
}

export function ProviderPaymentForm({ env, providers, onClose }: { env: string; providers: MessagingProvider[]; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [provider, setProvider] = useState(providers[0]?.key ?? '');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('EGP');
  const [paidAt, setPaidAt] = useState('');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const { fx } = useFxRates(env);
  const { scale } = useCreditScale(env);
  const parsed = parseMajorToMinor(amount, currency.toUpperCase());
  const paidIso = localInputToIso(paidAt);
  const asCredits = parsed.ok ? moneyAsCredits(fx, parsed.minor, currency.toUpperCase(), paidIso ?? undefined) : null;
  // A money write carries a reason (CP5).
  const valid = provider !== '' && parsed.ok && parsed.minor > 0 && /^[A-Za-z]{3}$/.test(currency) && reasonOk(reason);
  const save = useMutation({
    mutationFn: () => messagingApi.recordProviderPayment(env, {
      provider_key: provider, amount_minor: parsed.ok ? parsed.minor : 0, currency: currency.toUpperCase(),
      paid_at: paidIso ?? undefined, reference: reference.trim() || undefined,
      // messaging's provider-payment body has no `reason` field yet (it drops unknown fields), so the reason is also kept
      // on the note — the one free-text field it stores — until it does.
      note: [note.trim(), `Reason: ${reason.trim()}`].filter(Boolean).join(' · '),
      reason: reason.trim(),
    }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['ops', 'messaging', env, 'spend'] }); onClose(); },
  });
  return (
    <Modal title="Record a payment to a provider" onClose={save.isPending ? undefined : onClose} dismissible={!save.isPending}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={save.isPending}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Record payment'}</button>
        </>
      }>
      <p className="t-caption muted" style={{ margin: 0 }}>A record of money WE paid the provider (a prepayment or a settled invoice) — no money moves from here.</p>
      <div className="form-grid">
        <Select label="Provider" value={provider} onChange={setProvider} searchable={false} options={providers.map((p) => ({ value: p.key, label: p.display_name }))} />
        <TextField label={`Amount (${currency.toUpperCase()})`} value={amount} onChange={setAmount} error={amount && !parsed.ok ? parsed.error : null}
          hint={asCredits !== null ? `≈ ${formatCredits(asCredits, scale)} at billing's FX rate` : fx ? 'No billing FX rate for this currency.' : undefined} />
        <TextField label="Currency" value={currency} onChange={setCurrency} mono />
        <TextField label="Paid at (your local time)" value={paidAt} onChange={setPaidAt} type="datetime-local" hint="Empty = now." />
        <TextField label="Reference" value={reference} onChange={setReference} />
      </div>
      <TextField label="Note" value={note} onChange={setNote} />
      <ReasonField value={reason} onChange={setReason} />
      <MutationError error={save.error} service="messaging" />
    </Modal>
  );
}

/** A complete YYYY-MM-DD — the services refuse anything else with a 400. */
function isFullDay(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(value));
}

// ─── Delivery receipts ────────────────────────────────────────────────────

function DlrPanel({ env }: { env: string }) {
  const [from, setFrom] = useState(isoDay(-7));
  const [to, setTo] = useState(isoDay(0));
  const stats = useQuery({
    queryKey: mk(env, 'dlr', from, to),
    queryFn: () => messagingApi.dlrStats(env, { from, to }),
    enabled: isFullDay(from) && isFullDay(to),
    ...LIVE,
  });
  return (
    <Section title="Delivery receipts" meta="never assumed delivered — undelivered and pending are counted as such">
      <div className="filter-grid" style={{ marginBottom: 16 }}>
        <TextField label="From" value={from} onChange={setFrom} mono placeholder="YYYY-MM-DD" />
        <TextField label="To" value={to} onChange={setTo} mono placeholder="YYYY-MM-DD" />
      </div>
      <QueryState query={stats} service="messaging">
        {(data) => (
          <div className="stack">
            {data.compliance && (data.compliance.missing_compliance_profiles.length > 0 || data.compliance.catch_all_route_without_profile) && (
              <div className="callout callout-warning" role="status">
                <span className="callout-icon" aria-hidden="true">!</span>
                <div className="t-body">
                  <strong className="t-heading" style={{ display: 'block' }}>Countries routed without a compliance profile</strong>
                  Marketing there is unrestricted: {data.compliance.missing_compliance_profiles.join(', ') || 'none listed'}
                  {data.compliance.catch_all_route_without_profile && ' · a catch-all (*) route exists with no profile behind it'}.
                </div>
              </div>
            )}
            {data.providers.length === 0 ? <div className="empty-state t-body">No messages in this range.</div> : (
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Provider</th><th className="num">Sent</th><th className="num">Delivered</th><th className="num">Failed</th><th className="num">Expired</th><th className="num">Pending</th><th className="num">Delivery rate</th><th className="num">Median DLR latency</th></tr></thead>
                  <tbody>
                    {data.providers.map((p) => (
                      <tr key={p.provider_key}>
                        <td className="mono">{p.provider_key}</td>
                        <td className="num">{formatNumber(p.sent)}</td>
                        <td className="num">{formatNumber(p.delivered)}</td>
                        <td className="num">{formatNumber(p.failed)}</td>
                        <td className="num">{formatNumber(p.expired)}</td>
                        <td className="num">{formatNumber(p.pending)}</td>
                        <td className="num">{p.sent > 0 ? formatRatio(p.delivered / p.sent) : '—'}</td>
                        <td className="num">{p.median_dlr_latency_ms === null ? '—' : formatMs(p.median_dlr_latency_ms)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </QueryState>
    </Section>
  );
}
